import { z } from 'zod'

// The flight school's briefing packs (issue #268): markdown documents with
// one `## EX 13B — Title` section per exercise. Pure, so the editor can run
// the same parse live that the API runs on save.

export const BRIEFING_LANGUAGES = ['fi', 'en', 'sv'] as const
export type BriefingLanguage = (typeof BRIEFING_LANGUAGES)[number]
export const BriefingLanguageSchema = z.enum(BRIEFING_LANGUAGES)

export const MAX_BRIEFING_BYTES = 1_000_000

export const BriefingPackInputSchema = z.object({
  title: z.string().trim().min(1).max(200),
  language: BriefingLanguageSchema,
  // null = open to every student
  courseCode: z.string().trim().min(1).max(40).nullable(),
  description: z.string().trim().max(1000).nullable().optional(),
  bodyMd: z.string().min(1).max(MAX_BRIEFING_BYTES),
})
export type BriefingPackInput = z.infer<typeof BriefingPackInputSchema>

export interface BriefingPackSummary {
  packId: string
  title: string
  language: BriefingLanguage
  courseCode: string | null
  courseName: string | null
  description: string | null
  updatedAt: string
  canManage: boolean
}

export interface BriefingSection {
  anchor: string
  heading: string
  // The H1 (module) heading this section follows, if any.
  part: string | null
  // From the `<!-- lessons: ... -->` marker; empty when there is none.
  markerLessons: string[]
  hasMarker: boolean
  body: string
}

export interface BriefingPack extends BriefingPackSummary {
  bodyMd: string
  sections: BriefingSection[]
  lessons: { lessonId: number; lessonNumber: string; anchor: string }[]
}

export interface BriefingForLesson {
  packId: string
  title: string
  language: BriefingLanguage
  anchor: string
}

const CONTENTS_HEADING = /^(contents|sisällys|innehåll)$/i
const MARKER = /<!--\s*lessons:\s*([^>]*?)\s*-->/i

export function slugify(text: string): string {
  const slug = text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || 'section'
}

// A link target to a section. Authors' links follow GitHub's slugs
// (`#ex-13a--circuit`), ours collapse runs of hyphens, so compare without
// them, and accept a target that is the start of one section's anchor.
export function findSectionIndex(sections: { anchor: string }[], target: string): number {
  const norm = (a: string) => a.toLowerCase().replace(/-+/g, '-').replace(/^-|-$/g, '')
  const t = norm(target)
  if (!t) return -1
  const exact = sections.findIndex((s) => norm(s.anchor) === t)
  return exact >= 0 ? exact : sections.findIndex((s) => norm(s.anchor).startsWith(`${t}-`))
}

export function parseLessonList(text: string): string[] {
  return text
    .split(/[,;\s]+/)
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean)
}

export interface ParsedBriefing {
  title: string | null
  sections: BriefingSection[]
}

// Splits at `## ` headings (the first `# ` is the pack title, later ones
// are module labels that end a section). Headings inside code fences are
// not headings.
export function parseBriefingMarkdown(md: string): ParsedBriefing {
  const lines = md.replace(/\r\n?/g, '\n').split('\n')
  let title: string | null = null
  let part: string | null = null
  let fence = false
  interface RawSection {
    heading: string
    part: string | null
    lines: string[]
  }
  let current: RawSection | null = null
  const raw: RawSection[] = []
  for (const line of lines) {
    if (/^\s*(```|~~~)/.test(line)) fence = !fence
    const h1 = fence ? null : /^#\s+(.+?)\s*#*\s*$/.exec(line)
    const h2 = fence ? null : /^##\s+(.+?)\s*#*\s*$/.exec(line)
    if (h1) {
      if (current) raw.push(current)
      current = null
      if (title === null) title = h1[1]!
      else part = h1[1]!
    } else if (h2) {
      if (current) raw.push(current)
      current = { heading: h2[1]!, part, lines: [] }
    } else if (current) {
      current.lines.push(line)
    }
  }
  if (current) raw.push(current)

  const used = new Set<string>()
  const sections: BriefingSection[] = []
  for (const r of raw) {
    if (CONTENTS_HEADING.test(r.heading)) continue
    const body = r.lines.join('\n')
    const marker = MARKER.exec(body)
    let anchor = slugify(r.heading)
    for (let n = 2; used.has(anchor); n++) anchor = `${slugify(r.heading)}-${n}`
    used.add(anchor)
    sections.push({
      anchor,
      heading: r.heading,
      part: r.part,
      markerLessons: marker ? parseLessonList(marker[1]!) : [],
      hasMarker: marker !== null,
      body: body.replace(MARKER, '').replace(/^\n+|\s+$/g, ''),
    })
  }
  return { title, sections }
}

// The lesson numbers a heading names: `EX 13A`, `EX 13B–C`, `EX 12/13D`,
// `EX 18A1–12`. Only numbers the syllabus has are returned.
export function suggestLessons(heading: string, lessonNumbers: string[]): string[] {
  const known = new Set(lessonNumbers.map((n) => n.toUpperCase()))
  const spec = /^EX\s+([0-9A-Za-z/,–—-]+)/i.exec(heading)?.[1]?.toUpperCase()
  if (!spec) return []
  const found: string[] = []
  const add = (n: string) => {
    if (known.has(n) && !found.includes(n)) found.push(n)
  }
  for (const part of spec.split(/[/,]/).filter(Boolean)) {
    const [left, right] = part.split(/[–—-]/)
    if (!left) continue
    if (right === undefined || right === '') {
      add(left)
      continue
    }
    const num = /^(.*?)(\d+)$/.exec(left)
    if (/^\d+$/.test(right) && num) {
      // 18A1–12
      for (let n = Number(num[2]); n <= Number(right) && n < 1000; n++) add(`${num[1]}${n}`)
      continue
    }
    const letters = /^(\d+)([A-Z])$/.exec(left)
    const end = /^(?:\d+)?([A-Z])$/.exec(right)
    if (letters && end) {
      // 13B–C or 13B–13D
      for (let c = letters[2]!.charCodeAt(0); c <= end[1]!.charCodeAt(0); c++) add(`${letters[1]}${String.fromCharCode(c)}`)
      continue
    }
    add(left)
    add(right)
  }
  return found
}

export interface ResolvedBriefing {
  sections: (BriefingSection & { lessonNumbers: string[]; suggested: boolean })[]
  links: { lessonNumber: string; anchor: string }[]
  // Sections that brief no lesson (fine for an intro, a warning otherwise).
  unmatched: string[]
  // Marker numbers the syllabus does not have: { anchor, lessonNumber }.
  unknown: { anchor: string; lessonNumber: string }[]
  // A lesson claimed by two sections: the first keeps it.
  duplicates: { lessonNumber: string; anchor: string }[]
}

export function resolveBriefing(parsed: ParsedBriefing, lessonNumbers: string[]): ResolvedBriefing {
  const known = new Set(lessonNumbers.map((n) => n.toUpperCase()))
  const claimed = new Map<string, string>()
  const out: ResolvedBriefing = { sections: [], links: [], unmatched: [], unknown: [], duplicates: [] }
  for (const s of parsed.sections) {
    let numbers = s.markerLessons.filter((n) => known.has(n))
    for (const n of s.markerLessons) if (!known.has(n)) out.unknown.push({ anchor: s.anchor, lessonNumber: n })
    const suggested = !s.hasMarker
    if (suggested) numbers = suggestLessons(s.heading, lessonNumbers)
    const kept: string[] = []
    for (const n of numbers) {
      if (claimed.has(n)) out.duplicates.push({ lessonNumber: n, anchor: s.anchor })
      else {
        claimed.set(n, s.anchor)
        kept.push(n)
        out.links.push({ lessonNumber: n, anchor: s.anchor })
      }
    }
    if (kept.length === 0) out.unmatched.push(s.anchor)
    out.sections.push({ ...s, lessonNumbers: kept, suggested })
  }
  return out
}

// Writes the marker into every section that has none, from the suggestion,
// so an imported pack is self-describing from then on.
export function applySuggestedMarkers(md: string, resolved: ResolvedBriefing): string {
  const lines = md.replace(/\r\n?/g, '\n').split('\n')
  const byHeading = new Map(resolved.sections.filter((s) => s.suggested && s.lessonNumbers.length > 0).map((s) => [s.heading, s]))
  const out: string[] = []
  let fence = false
  for (const line of lines) {
    out.push(line)
    if (/^\s*(```|~~~)/.test(line)) fence = !fence
    const h2 = fence ? null : /^##\s+(.+?)\s*#*\s*$/.exec(line)
    const s = h2 ? byHeading.get(h2[1]!) : undefined
    if (s) {
      out.push(`<!-- lessons: ${s.lessonNumbers.join(', ')} -->`)
      byHeading.delete(h2![1]!)
    }
  }
  return out.join('\n')
}

// --- Template --------------------------------------------------------------

export interface BriefingTemplateCourse {
  courseCode: string
  name: string
  modules: {
    moduleNumber: number
    title: string
    lessons: { lessonNumber: string; title: string; items: { description: string }[] }[]
  }[]
}

const TEMPLATE_TEXT: Record<
  BriefingLanguage,
  { pack: string; intro: string; contents: string; module: string; aim: string; key: string; picture: string; tem: string; questions: string }
> = {
  en: {
    pack: 'briefings',
    intro: 'Briefings for the course. Replace the prompts under each heading; keep the `lessons` marker under each `## EX` heading, it links the section to the syllabus.',
    contents: 'Contents',
    module: 'Module',
    aim: 'Aim',
    key: 'Key points',
    picture: 'Picture',
    tem: 'Airmanship · TEM',
    questions: 'Questions',
  },
  fi: {
    pack: 'briefingit',
    intro: 'Kurssin briefingit. Korvaa otsikoiden alla olevat ohjeet; säilytä `lessons`-merkintä jokaisen `## EX` -otsikon alla, se yhdistää osion opetusohjelmaan.',
    contents: 'Sisällys',
    module: 'Moduuli',
    aim: 'Tavoite',
    key: 'Pääkohdat',
    picture: 'Kuva',
    tem: 'Ilmailutaito · TEM',
    questions: 'Kysymykset',
  },
  sv: {
    pack: 'genomgångar',
    intro: 'Kursens genomgångar. Ersätt anvisningarna under rubrikerna; behåll `lessons`-markeringen under varje `## EX`-rubrik, den länkar avsnittet till kursplanen.',
    contents: 'Innehåll',
    module: 'Modul',
    aim: 'Mål',
    key: 'Huvudpunkter',
    picture: 'Bild',
    tem: 'Flygkunnande · TEM',
    questions: 'Frågor',
  },
}

export function buildBriefingTemplate(course: BriefingTemplateCourse, lang: BriefingLanguage): string {
  const t = TEMPLATE_TEXT[lang]
  const out: string[] = [`# ${course.name} — ${t.pack}`, '', t.intro, '', `## ${t.contents}`, '']
  for (const m of course.modules) {
    out.push(`- ${t.module} ${m.moduleNumber} — ${m.title}`)
    for (const l of m.lessons) out.push(`  - EX ${l.lessonNumber} — ${l.title}`)
  }
  for (const m of course.modules) {
    out.push('', `# ${t.module} ${m.moduleNumber} — ${m.title}`)
    for (const l of m.lessons) {
      out.push('', `## EX ${l.lessonNumber} — ${l.title}`, `<!-- lessons: ${l.lessonNumber} -->`, '', `### ${t.aim}`, '', '')
      out.push(`### ${t.key}`, '')
      if (l.items.length === 0) out.push('- ')
      for (const i of l.items) out.push(`- ${i.description}`)
      out.push('', `### ${t.picture}`, '', '', `### ${t.tem}`, '', '', `### ${t.questions}`, '', '')
    }
  }
  return out.join('\n').replace(/\n{4,}/g, '\n\n\n') + '\n'
}
